import { useTranslation } from '@/i18n/useTranslation';

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewsApi } from '@/api';
import { InlineError } from '@/components/common/InlineError';

export function ReviewCatalogPage() {
  const { displayValue, t } = useTranslation();
  const cache = useQueryClient();
  const groups = useQuery({ queryKey: ['review-groups'], queryFn: reviewsApi.listGroups });
  const categories = useQuery({
    queryKey: ['review-categories'],
    queryFn: reviewsApi.listCategories,
  });
  const [groupCode, setGroupCode] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const refresh = () => {
    void cache.invalidateQueries({ queryKey: ['review-groups'] });
    void cache.invalidateQueries({ queryKey: ['review-categories'] });
  };
  const addGroup = useMutation({
    mutationFn: reviewsApi.createGroup,
    onSuccess: () => {
      setGroupCode('');
      setGroupName('');
      refresh();
    },
  });
  const addCategory = useMutation({
    mutationFn: reviewsApi.createCategory,
    onSuccess: () => {
      setCode('');
      setName('');
      refresh();
    },
  });
  const toggleGroup = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      reviewsApi.setGroupActive(id, active),
    onSuccess: refresh,
  });
  const toggleCategory = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      reviewsApi.setCategoryActive(id, active),
    onSuccess: refresh,
  });
  const removeGroup = useMutation({
    mutationFn: reviewsApi.deleteGroup,
    onSuccess: () => {
      setGroupId('');
      refresh();
    },
  });
  const removeCategory = useMutation({
    mutationFn: reviewsApi.deleteCategory,
    onSuccess: refresh,
  });
  const groupHasChoices = (id: string) =>
    categories.data?.some((category) => category.group_id === id) ?? false;
  const error =
    groups.error ??
    categories.error ??
    addGroup.error ??
    addCategory.error ??
    toggleGroup.error ??
    toggleCategory.error ??
    removeGroup.error ??
    removeCategory.error;
  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <header>
        <h1>{t('Admin · Review categories')}</h1>
        <p>
          {t(
            'Manage human correction choices. Model predictions and inspection defect types are separate.',
          )}
        </p>
      </header>
      {error && <InlineError error={error} />}
      <section className="glass-panel stack">
        <h2>{t('Category groups')}</h2>
        <p>
          {t(
            'The initial group is Defect. Additional groups can be added for future review workflows.',
          )}
        </p>
        <form
          className="cluster"
          onSubmit={(e) => {
            e.preventDefault();
            addGroup.mutate({ code: groupCode, name: groupName });
          }}
        >
          <label className="field">
            {t('Code')}
            <input
              className="input"
              required
              pattern="[a-z][a-z0-9_]+"
              maxLength={64}
              value={groupCode}
              onChange={(e) => setGroupCode(e.target.value)}
            />
          </label>
          <label className="field">
            {t('Name')}
            <input
              className="input"
              required
              maxLength={128}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </label>
          <button type="submit" className="btn btn--primary btn--sm" disabled={addGroup.isPending}>
            {t('Add group')}
          </button>
        </form>
        <ul>
          {groups.data?.map((group) => (
            <li key={group.id}>
              {displayValue(group.name)} ({group.code}) ·{' '}
              {group.is_active ? t('Active') : t('Inactive')}{' '}
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={toggleGroup.isPending}
                onClick={() => toggleGroup.mutate({ id: group.id, active: !group.is_active })}
              >
                {group.is_active ? t('Deactivate') : t('Activate')}
              </button>{' '}
                {!group.is_active && (
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  disabled={removeGroup.isPending || groupHasChoices(group.id)}
                  title={
                    groupHasChoices(group.id)
                      ? t('Delete the group’s correction choices first.')
                      : undefined
                  }
                  onClick={() => {
                    if (window.confirm(t('Delete this group? This cannot be undone.'))) {
                      removeGroup.mutate(group.id);
                    }
                  }}
                >
                  {t('Delete')}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section className="glass-panel stack">
        <h2>{t('Correction choices')}</h2>
        <form
          className="cluster"
          onSubmit={(e) => {
            e.preventDefault();
            addCategory.mutate({ group_id: groupId, code, name });
          }}
        >
          <label className="field">
            {t('Group')}
            <select
              className="select"
              required
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">{t('Select…')}</option>
              {groups.data
                ?.filter((g) => g.is_active)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {displayValue(g.name)}
                  </option>
                ))}
            </select>
          </label>
          <label className="field">
            {t('Code')}
            <input
              className="input"
              required
              pattern="[a-z][a-z0-9_]+"
              maxLength={64}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          <label className="field">
            {t('Name')}
            <input
              className="input"
              required
              maxLength={128}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <button
            type="submit"
            className="btn btn--primary btn--sm"
            disabled={addCategory.isPending}
          >
            {t('Add choice')}
          </button>
        </form>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('Group')}</th>
                <th>{t('Choice')}</th>
                <th>{t('Code')}</th>
                <th>{t('Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {categories.data?.map((category) => (
                <tr key={category.id}>
                  <td>{displayValue(category.group_code)}</td>
                  <td>{displayValue(category.name)}</td>
                  <td>{category.code}</td>
                  <td>
                    {!category.group_is_active
                      ? t('Group inactive')
                      : category.is_active
                        ? t('Active')
                        : t('Inactive')}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled={toggleCategory.isPending}
                      onClick={() =>
                        toggleCategory.mutate({ id: category.id, active: !category.is_active })
                      }
                    >
                      {category.is_active ? t('Deactivate') : t('Activate')}
                    </button>{' '}
                    {!category.is_active && (
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        disabled={removeCategory.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              t('Delete this correction choice? This cannot be undone.'),
                            )
                          ) {
                            removeCategory.mutate(category.id);
                          }
                        }}
                      >
                        {t('Delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
